import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinTextField,
  AddButton,
  ThinDropdownWithSearch,
  BackButton,
} from "../../../components/common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Loader from "../../../components/partials/Loader";

const AddVillage = () => {
  const [loader, setLoader] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [dehs, setDehs] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchDistricts = async () => {
      setLoader(true);
      try {
        const res = await fetchData(
          "districts",
          "GET",
          null,
          {},
          {},
          navigate,
          "/districts",
          dispatch
        );
        setDistricts(res?.data || []);
      } catch (error) {
        toast.error("Failed to load districts");
      } finally {
        setLoader(false);
      }
    };
    fetchDistricts();
  }, []);

  const validationSchema = Yup.object({
    village_name: Yup.string().required("Village Name is required"),
    district_id: Yup.string().required("District is required"),
    tehsil_id: Yup.string().required("Tehsil is required"),
    deh_id: Yup.string().required("Deh is required"),
    status: Yup.number().required("Status is required"),
    type: Yup.string().required("Type is required"),
  });

  const formik = useFormik({
    initialValues: {
      village_name: "",
      district_id: "",
      tehsil_id: "",
      deh_id: "",
      status: 1,
      type: "Rural",
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      const data = {
        village_name: values.village_name,
        district_id: parseInt(values.district_id),
        tehsil_id: parseInt(values.tehsil_id),
        deh_id: parseInt(values.deh_id),
        status: parseInt(values.status),
        type: values.type,
      };

      try {
        const res = await fetchData(
          "villages",
          "POST",
          data,
          {},
          {},
          navigate,
          "/villages",
          dispatch
        );

        const vid = res?.village?.vid;
        toast.success(
          vid ? `Village created successfully (VID: ${vid})` : "Village created successfully"
        );
        navigate("/villages");
      } catch (error) {
        toast.error(error.message || "Failed to create Village");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleDistrictChange = async (selectedValue) => {
    formik.setValues((prev) => ({
      ...prev,
      district_id: selectedValue,
      tehsil_id: "",
      deh_id: "",
    }));

    if (selectedValue) {
      formik.setFieldError("district_id", undefined);
    }

    if (selectedValue) {
      try {
        setLoader(true);
        const res = await fetchData(
          "tehsils",
          "GET",
          null,
          {},
          { district_id: selectedValue },
          navigate,
          "/tehsils",
          dispatch
        );
        setTehsils(res?.data || []);
        setDehs([]);
      } catch (error) {
        //toast.error("Failed to load tehsils");
      } finally {
        setLoader(false);
      }
    } else {
      setTehsils([]);
      setDehs([]);
    }
  };

  const handleTehsilChange = async (selectedValue) => {
    formik.setValues((prev) => ({
      ...prev,
      tehsil_id: selectedValue,
      deh_id: "",
    }));

    if (selectedValue) {
      formik.setFieldError("tehsil_id", undefined);
    }

    if (selectedValue) {
      try {
        setLoader(true);
        const res = await fetchData(
          "dehs",
          "GET",
          null,
          {},
          { tehsil_id: selectedValue },
          navigate,
          "/dehs",
          dispatch
        );
        setDehs(res?.data || []);
      } catch (error) {
        setDehs([]);
        //toast.error("Failed to load Dehs");
      } finally {
        setLoader(false);
      }
    } else {
      setDehs([]);
    }
  };

  const handleDehChange = (selectedValue) => {
    formik.setFieldValue("deh_id", selectedValue);
    
    if (selectedValue) {
      formik.setFieldError("deh_id", undefined);
    }
  };

  return (
    <div className="w-full my-4 mx-auto">
      <div className="flex justify-between items-center gap-3 w-full">
        <h1 className="text-black text-[1.475rem] font-[Bold]">
          Create Village
        </h1>
        <BackButton text="Back" to="/villages" />
      </div>

      <div className="bg-white rounded-lg shadow w-full mt-4 mb-[7rem]">
        <div className="p-4 flex justify-between items-center border-b border-lightGray gradiant2 rounded-t-lg">
          <h1 className="lg:text-[0.875rem] text-[13px] uppercase text-[#856800] font-bold">
            Create Village
          </h1>
        </div>

        <form onSubmit={formik.handleSubmit} className="p-4">
          <div className="grid grid-cols-2 gap-4">
            <ThinDropdownWithSearch
              label="District"
              options={districts.map((d) => ({
                value: d.district_id,
                label: d.district_name,
              }))}
              value={formik.values.district_id}
              onChange={handleDistrictChange}
              onBlur={() => formik.setFieldTouched("district_id", true)}
              error={formik.touched.district_id && formik.errors.district_id}
              helperText={
                formik.touched.district_id && formik.errors.district_id
              }
            />

            <ThinDropdownWithSearch
              label="Tehsil"
              options={tehsils.map((t) => ({
                value: t.tehsil_id,
                label: t.tehsil_name,
              }))}
              value={formik.values.tehsil_id}
              onChange={handleTehsilChange}
              onBlur={() => formik.setFieldTouched("tehsil_id", true)}
              disabled={!formik.values.district_id}
              error={formik.touched.tehsil_id && formik.errors.tehsil_id}
              helperText={
                formik.touched.tehsil_id && formik.errors.tehsil_id
              }
            />

            <ThinDropdownWithSearch
              label="Deh"
              options={dehs.map((d) => ({
                value: d.deh_id,
                label: d.deh_name,
              }))}
              value={formik.values.deh_id}
              onChange={handleDehChange}
              onBlur={() => formik.setFieldTouched("deh_id", true)}
              disabled={!formik.values.tehsil_id}
              error={formik.touched.deh_id && formik.errors.deh_id}
              helperText={
                formik.touched.deh_id && formik.errors.deh_id
              }
            />

            <ThinTextField
              label="Village Name"
              name="village_name"
              value={formik.values.village_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.village_name && formik.errors.village_name}
              helperText={
                formik.touched.village_name && formik.errors.village_name
              }
            />

            <ThinDropdownWithSearch
              label="Status"
              options={[
                { value: 1, label: "Active" },
                { value: 0, label: "In-Active" },
              ]}
              value={formik.values.status}
              onChange={(val) => formik.setFieldValue("status", val)}
              error={formik.touched.status && formik.errors.status}
            />

            <ThinDropdownWithSearch
              label="Type"
              options={[
                { value: "Rural", label: "Rural" },
                { value: "Urban", label: "Urban" },
              ]}
              value={formik.values.type}
              onChange={(val) => formik.setFieldValue("type", val)}
              error={formik.touched.type && formik.errors.type}
              helperText={formik.touched.type && formik.errors.type}
            />
          </div>

          <div className="p-4">
            <AddButton
              text="Submit"
              type="Submit"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
      {loader && <Loader />}
    </div>
  );
};

export default AddVillage;
